/**
 * A point in both Cartesian (x, y) and barycentric (a, b, c) coordinates
 */
export type TriangulatedPoint = {
  /** The "x" Cartesian coordinate */
  x: number

  /** The "y" Cartesian coordinate */
  y: number

  /** The "a" barycentric coordinate */
  a: number

  /** The "b" barycentric coordinate */
  b: number

  /** The "c" barycentric coordinate */
  c: number
}

// BufferGeometry UVs are stored as Float32, so points that land exactly on a
// triangle edge/vertex can pick up small negative barycentric values.
const barycentricCoordinateTolerance = -1e-12
/**
 * Returns whether a triangulated point is within its triangle using
 * simple barycentric math with a small error tolerance
 * @param point The point in question
 */
export const isInsideTriangle = (point: TriangulatedPoint) =>
  point.a >= barycentricCoordinateTolerance &&
  point.b >= barycentricCoordinateTolerance &&
  point.c >= barycentricCoordinateTolerance

/**
 * Creates a point from Cartesian coordinates and a triangle (a, b, c)
 * @param x The "x" Cartesian coordinate
 * @param y The "y" Cartesian coordinate
 * @param ax The x coordinate of the triangle point "a"
 * @param ay The y coordinate of the triangle point "a"
 * @param bx The x coordinate of the triangle point "b"
 * @param by The y coordinate of the triangle point "b"
 * @param cx The x coordinate of the triangle point "c"
 * @param cy The y coordinate of the triangle point "c"
 */
export const cart = (
  x: number,
  y: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number
): TriangulatedPoint => {
  const v0x = bx - ax
  const v0y = by - ay
  const v1x = cx - ax
  const v1y = cy - ay
  const v2x = x - ax
  const v2y = y - ay

  const d00 = v0x * v0x + v0y * v0y
  const d01 = v0x * v1x + v0y * v1y
  const d11 = v1x * v1x + v1y * v1y
  const d20 = v2x * v0x + v2y * v0y
  const d21 = v2x * v1x + v2y * v1y

  const invDenominator = 1.0 / (d00 * d11 - d01 * d01) // Faster than division
  const b = (d11 * d20 - d01 * d21) * invDenominator
  const c = (d00 * d21 - d01 * d20) * invDenominator
  const a = 1.0 - b - c

  return { x, y, a, b, c }
}

/**
 Creates a point from Barycentric coordinates and a triangle (a, b, c)
 * @param a The "a" barycentric coordinate
 * @param b The "b" barycentric coordinate
 * @param c The "c" barycentric coordinate
 * @param ax The x coordinate of the triangle point "a"
 * @param ay The y coordinate of the triangle point "a"
 * @param bx The x coordinate of the triangle point "b"
 * @param by The y coordinate of the triangle point "b"
 * @param cx The x coordinate of the triangle point "c"
 * @param cy The y coordinate of the triangle point "c"
 */
export const bary = (
  a: number,
  b: number,
  c: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number
): TriangulatedPoint => {
  const x = ax * a + bx * b + cx * c
  const y = ay * a + by * b + cy * c
  return { x, y, a, b, c }
}

/**
 * A simple method for creating a point from Cartesian and barycentric coords
 * @param x The "x" Cartesian coordinate
 * @param y The "y" Cartesian coordinate
 * @param a The "a" barycentric coordinate
 * @param b The "b" barycentric coordinate
 * @param c The "c" barycentric coordinate
 */
export const cartBary = (
  x: number,
  y: number,
  a: number,
  b: number,
  c: number
): TriangulatedPoint => ({ x, y, a, b, c })

//------------------------------------------------------------------------------

/**
 * Returns a relative position "t" along line segment (a->b) where it intersects
 * line segment (c->d), including if the intersection is at an endpoint,
 *  or -1, if there is no such intersection
 * @param ax The x-coordinate of the first line's start
 * @param ay The y-coordinate of the first line's start
 * @param bx The x-coordinate of the first line's end
 * @param by The y-coordinate of the first line's end
 * @param cx The x-coordinate of the second line's start
 * @param cy The y-coordinate of the second line's start
 * @param dx The x-coordinate of the second line's end
 * @param dy The y-coordinate of the second line's end
 */
export const intersectLines = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number
): number => {
  const abx = ax - bx
  const aby = ay - by
  const cdx = cx - dx
  const cdy = cy - dy

  const d = abx * cdy - aby * cdx
  if (d === 0.0) {
    // Parallel or coincident
    return -1.0
  }

  const acx = ax - cx
  const acy = ay - cy
  const t = (acx * cdy - acy * cdx) / d
  if (
    t - barycentricCoordinateTolerance < 0 ||
    t + barycentricCoordinateTolerance > 1
  ) {
    // Not within the (a -> b) line segment
    return -1.0
  }

  const u = -(abx * acy - aby * acx) / d
  if (
    u - barycentricCoordinateTolerance < 0 ||
    u + barycentricCoordinateTolerance > 1
  ) {
    // Not within the (c -> d) line segment
    return -1.0
  }

  return Math.max(Math.min(t, 1.0), 0.0)
}

/**
 * Math which determines whether a Cartesian point (px, py) is within a polygon,
 * also described in Cartesian coordinates
 * @param x The x coordinate of the point to check
 * @param y The y coordinate of the point to check
 * @param polygon An array of Cartesian points representing the polygon hull
 */
export function isPointInPolygon(
  x: number,
  y: number,
  polygon: ArrayLike<number>
): boolean {
  let isInside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const y1 = polygon[i * 2 + 1]
    const x1 = polygon[i * 2]
    const y2 = polygon[j * 2 + 1]
    const x2 = polygon[j * 2]
    // imagine a case in which a ray is drawn to the right but hits a corner
    // connecting two line segments, this point would not be included
    // unless we check for whether y is equal to one of the other ysg
    if (y1 > y !== y2 > y || y1 === y || y2 === y) {
      const xOnLine = ((x2 - x1) * (y - y1)) / (y2 - y1) + x1
      if (x < xOnLine) isInside = !isInside
      else if (x === xOnLine) return true
    }
  }
  return isInside
}

/**
 * Checks whether a given triangle with points (a, b, c) is degenerate, with
 * all of its points collinear
 * @param ax The x coordinate of the triangle point "A"
 * @param ay The y coordinate of the triangle point "A"
 * @param az The z coordinate of the triangle point "A"
 * @param bx The x coordinate of the triangle point "B"
 * @param by The y coordinate of the triangle point "B"
 * @param bz The z coordinate of the triangle point "B"
 * @param cx The x coordinate of the triangle point "C"
 * @param cy The y coordinate of the triangle point "C"
 * @param cz The z coordinate of the triangle point "C"
 * @param tolerance A tolerance value used to address floating-point errors
 */
export function isTriangleCollinear(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number,
  tolerance: number
): boolean {
  const abx = bx - ax
  const aby = by - ay
  const abz = bz - az
  const acx = cx - ax
  const acy = cy - ay
  const acz = cz - az
  const cpi = aby * acz - abz * acy
  const cpj = -abx * acz + abz * acx
  const cpk = abx * acy - aby * acx
  return (
    Math.abs(cpi) <= tolerance &&
    Math.abs(cpj) <= tolerance &&
    Math.abs(cpk) <= tolerance
  )
}
